import { useMemo, useState } from 'react';
import type { DomainSearchResults } from '@prerender/design-system/SingleDomainPicker';

import { useGetDomainsQuery } from './domainManagerApiSlice';

/** `/v3/domains` stops at this many rows and does not say whether it did. */
export const DOMAIN_SEARCH_ROW_CAP = 1000;

export type DomainSearch = DomainSearchResults & {
  search: (query: string) => void;
};

function isCapped(query: string, rows: number, totalCount: number): boolean {
  return query ? rows >= DOMAIN_SEARCH_ROW_CAP : totalCount > rows;
}

/**
 * The term goes to the server: a client-side filter over a capped result can never
 * reach a domain past `DOMAIN_SEARCH_ROW_CAP`.
 */
export function useDomainSearch(): DomainSearch {
  const [requestedQuery, setRequestedQuery] = useState('');
  // `data` keeps the last successful result across query changes, unlike `currentData`,
  // so the option list does not blank out between keystrokes.
  const { data, isFetching, isError } = useGetDomainsQuery(requestedQuery);
  // The server reports the account's real size only when nothing is searched.
  const unfiltered = useGetDomainsQuery('');

  const [answeredQuery, setAnsweredQuery] = useState(requestedQuery);
  if (!isFetching && answeredQuery !== requestedQuery) {
    setAnsweredQuery(requestedQuery);
  }

  const domains = useMemo(() => (isError ? [] : data?.domains.map(({ domain }) => domain) ?? []), [data, isError]);

  return {
    domains,
    query: answeredQuery,
    loading: Boolean(isFetching),
    error: Boolean(isError),
    capped: !isError && isCapped(answeredQuery, domains.length, data?.totalCount ?? 0),
    total: unfiltered.isError ? undefined : unfiltered.data?.totalCount,
    search: setRequestedQuery,
  };
}
